/**
 * Facet mark + wordmark — redesign.
 * The mark is a nested octagon ("cut facet" in plan view): an ink outer ring with
 * a finer blue inner ring. On dark surfaces the `bone` variant swaps to
 * bone/soft-blue. Ported verbatim from the reference design SVG.
 */
import type { CSSProperties } from 'react';

type Variant = 'ink' | 'bone';

const palette: Record<Variant, { outer: string; inner: string }> = {
  ink: { outer: '#16181d', inner: '#2547e0' },
  bone: { outer: '#f2f2ee', inner: '#9db1f5' },
};

export function FacetMark({
  size = 26,
  variant = 'ink',
  withLines = false,
  title,
}: {
  size?: number;
  variant?: Variant;
  /** Draw the four axis ticks between the rings (used on the large hero/CTA marks). */
  withLines?: boolean;
  title?: string;
}) {
  const c = palette[variant];
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 38 38"
      role={title ? 'img' : undefined}
      aria-label={title || undefined}
      aria-hidden={title ? undefined : true}
      focusable="false"
      style={{ flex: 'none' }}
    >
      <polygon
        points="19,1 31.7,6.3 37,19 31.7,31.7 19,37 6.3,31.7 1,19 6.3,6.3"
        fill="none"
        stroke={c.outer}
        strokeWidth={1}
      />
      <polygon
        points="19,10 25.4,12.6 28,19 25.4,25.4 19,28 12.6,25.4 10,19 12.6,12.6"
        fill="none"
        stroke={c.inner}
        strokeWidth={0.65}
      />
      {withLines && (
        <g stroke={c.outer} strokeWidth={0.4}>
          <line x1="19" y1="1" x2="19" y2="10" />
          <line x1="19" y1="28" x2="19" y2="37" />
          <line x1="1" y1="19" x2="10" y2="19" />
          <line x1="28" y1="19" x2="37" y2="19" />
        </g>
      )}
    </svg>
  );
}

/** Horizontal lockup: octagon mark + Archivo wordmark. */
export function FacetLockup({
  size = 26,
  variant = 'ink',
  style,
}: {
  size?: number;
  variant?: Variant;
  style?: CSSProperties;
}) {
  const color = variant === 'bone' ? '#f2f2ee' : '#16181d';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, ...style }}>
      <FacetMark size={size} variant={variant} />
      <span
        style={{
          fontFamily: "'Archivo', system-ui, sans-serif",
          fontWeight: 600,
          fontSize: size * 0.73,
          letterSpacing: '-0.02em',
          color,
        }}
      >
        Facet
      </span>
    </span>
  );
}
